import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  message,
  Alert,
  Tooltip,
  Typography,
  Modal,
} from "antd";
import {
  ShoppingCartOutlined,
  LinkOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Booking } from "../../types/booking";
import { PicqerService } from "../../services/picqerService";
import { formatDate } from "../../utils/bookingUtils";
import { supabase } from "../../lib/supabase";

const { Text } = Typography;

interface PurchaseOrderSectionProps {
  booking: Booking;
  onRefresh: () => void;
}

interface PurchaseOrder {
  id: number;
  picqer_reference: string;
  booking_reference: string;
  picqer_purchase_order_id: number;
  picqer_purchaseorderid: string;
  status: string;
  supplier_name?: string | null;
  created_at: string;
}

interface ProductAnalysis {
  id: number;
  name: string;
  ean: string | null;
  pieces: number | null;
  cartons: number | null;
  hasEAN: boolean;
  hasCompanyProduct: boolean;
  hasPicqerProduct: boolean;
  picqerProductId: number | null;
  companyProductId: number | null;
  status: "ready" | "needs_picqer_link" | "needs_company_product" | "needs_ean";
  canInclude: boolean;
}

interface ProductAnalysisResponse {
  total: number;
  ready: number;
  needingSetup: number;
  products: ProductAnalysis[];
}

const PurchaseOrderSection: React.FC<PurchaseOrderSectionProps> = ({
  booking,
  onRefresh,
}) => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [picqerIntegrationValid, setPicqerIntegrationValid] = useState(false);
  const [integrationError, setIntegrationError] = useState<string | null>(null);
  const [productAnalysis, setProductAnalysis] =
    useState<ProductAnalysisResponse | null>(null);
  const [loadingProductAnalysis, setLoadingProductAnalysis] = useState(false);
  const [deletingPurchaseOrder, setDeletingPurchaseOrder] = useState<
    number | null
  >(null);

  const isStorageBooking =
    !booking.booking_delivery_info ||
    (Array.isArray(booking.booking_delivery_info) &&
      booking.booking_delivery_info.length === 0);

  useEffect(() => {
    loadPurchaseOrders();
    checkPicqerIntegration();

    // For storage bookings, always fetch product analysis
    if (isStorageBooking) {
      loadProductAnalysis();
    }
  }, [booking.id, isStorageBooking]);

  const checkPicqerIntegration = async () => {
    try {
      const { data: companyPicqer, error: picqerError } = await supabase
        .from("company")
        .select("picqer_enabled, picqer_api_key, picqer_fulfilment_customer_id")
        .eq("id", booking.company_id)
        .single();

      if (picqerError || !companyPicqer) {
        setPicqerIntegrationValid(false);
        setIntegrationError("Unable to check Picqer integration");
        return;
      }

      if (!companyPicqer.picqer_enabled || !companyPicqer.picqer_api_key) {
        setPicqerIntegrationValid(false);
        setIntegrationError("Company not connected to Picqer");
        return;
      }

      if (!companyPicqer.picqer_fulfilment_customer_id) {
        setPicqerIntegrationValid(false);
        setIntegrationError("No fulfillment customer configured");
        return;
      }

      setPicqerIntegrationValid(true);
      setIntegrationError(null);
    } catch (error) {
      console.error("Error checking Picqer integration:", error);
      setPicqerIntegrationValid(false);
      setIntegrationError("Error checking integration");
    }
  };

  const loadPurchaseOrders = async () => {
    setLoading(true);
    try {
      const result = await PicqerService.getPurchaseOrdersForBooking(
        booking.id
      );
      if (result.success && result.data) {
        setPurchaseOrders(result.data);
      } else {
        console.error("Failed to load purchase orders:", result.error);
        setPurchaseOrders([]);
      }
    } catch (error) {
      console.error("Error loading purchase orders:", error);
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProductAnalysis = async () => {
    setLoadingProductAnalysis(true);
    try {
      console.log("Loading product analysis for booking:", booking.id);
      const result = await PicqerService.createPurchaseOrderForBooking(
        booking.id
      );

      console.log("Product analysis result:", result);

      // Always set product analysis if it exists, regardless of success status
      console.log("Full result object:", JSON.stringify(result, null, 2));
      console.log("Result keys:", Object.keys(result || {}));
      console.log("Has productAnalysis:", !!result.productAnalysis);
      console.log("productAnalysis value:", result.productAnalysis);

      if (result.productAnalysis) {
        console.log("Setting product analysis:", result.productAnalysis);
        console.log("Product analysis details:", {
          total: result.productAnalysis.total,
          ready: result.productAnalysis.ready,
          needingSetup: result.productAnalysis.needingSetup,
          productsCount: result.productAnalysis.products?.length,
        });
        setProductAnalysis(result.productAnalysis);
      } else {
        console.log("No product analysis in result, setting empty state");
        console.log("Available keys in result:", Object.keys(result));
        console.log("Checking for productAnalysis in result:", result);
        setProductAnalysis({
          total: 0,
          ready: 0,
          needingSetup: 0,
          products: [],
        });
      }
    } catch (error) {
      console.error("Error loading product analysis:", error);
      // Set empty analysis to show loading state
      setProductAnalysis({
        total: 0,
        ready: 0,
        needingSetup: 0,
        products: [],
      });
    } finally {
      setLoadingProductAnalysis(false);
    }
  };

  const handleDeletePurchaseOrder = async (
    purchaseOrderId: number,
    status: string
  ) => {
    // Prevent deletion of purchase orders that are already purchased or expected
    if (status === "purchased") {
      message.error(
        "Cannot delete a purchase order that has already been purchased. Please cancel it in Picqer first."
      );
      return;
    }

    if (status === "expected") {
      message.error(
        "Cannot delete a purchase order that is expected. Please cancel it in Picqer first."
      );
      return;
    }

    setDeletingPurchaseOrder(purchaseOrderId);
    try {
      const result = await PicqerService.deletePurchaseOrder(purchaseOrderId);

      if (result.success) {
        const productCount = result.deletedProductsCount || 0;
        message.success(
          `Purchase order deleted successfully${
            productCount > 0
              ? ` along with ${productCount} associated product${
                  productCount === 1 ? "" : "s"
                }`
              : ""
          }`
        );
        loadPurchaseOrders();
        onRefresh();
      } else {
        message.error(result.error || "Failed to delete purchase order");
      }
    } catch (error) {
      console.error("Error deleting purchase order:", error);
      message.error("An error occurred while deleting the purchase order");
    } finally {
      setDeletingPurchaseOrder(null);
    }
  };

  const handleCreatePurchaseOrder = async () => {
    if (!isStorageBooking) {
      message.error("Purchase orders can only be created for storage bookings");
      return;
    }

    setCreating(true);
    try {
      // First check if company has proper Picqer integration with fulfillment customer
      const { data: companyPicqer, error: picqerError } = await supabase
        .from("company")
        .select("picqer_enabled, picqer_api_key, picqer_fulfilment_customer_id")
        .eq("id", booking.company_id)
        .single();

      if (picqerError || !companyPicqer) {
        message.error("Unable to check Picqer integration. Please try again.");
        return;
      }

      if (!companyPicqer.picqer_enabled || !companyPicqer.picqer_api_key) {
        message.error(
          "Company not connected to Picqer. Please set up Picqer integration first in the Picqer Integration tab."
        );
        return;
      }

      if (!companyPicqer.picqer_fulfilment_customer_id) {
        message.error(
          "No Picqer fulfillment customer configured. Please configure a fulfillment customer in the Picqer Integration tab before creating purchase orders."
        );
        return;
      }

      // All validations passed, proceed with purchase order creation
      const result = await PicqerService.createPurchaseOrderForBooking(
        booking.id
      );

      console.log("Purchase order creation result:", result);

      if (result.success && result.data) {
        message.success(
          `Purchase order created successfully! Picqer: ${result.data.picqerReference}, Booking: ${result.data.bookingReference}`
        );

        // Store product analysis if provided
        if (result.productAnalysis) {
          setProductAnalysis(result.productAnalysis);
          if (result.productAnalysis.needingSetup > 0) {
            message.warning(
              `${result.productAnalysis.needingSetup} products need setup before they can be included in future purchase orders.`
            );
          }
        }

        loadPurchaseOrders();
        onRefresh();
      } else if (result.existingPurchaseOrder) {
        message.info(
          `Purchase order already exists: ${result.existingPurchaseOrder.reference}`
        );
        loadPurchaseOrders();
      } else {
        console.log("Entering else block - failed purchase order creation");
        console.log("Checking for productAnalysis:", !!result.productAnalysis);

        // Handle the case where no products are ready for purchase order
        if (result.productAnalysis) {
          console.log("Setting product analysis:", result.productAnalysis);
          setProductAnalysis(result.productAnalysis);

          if (result.productAnalysis.ready === 0) {
            message.error(
              `Cannot create purchase order: ${result.productAnalysis.needingSetup} products need Picqer integration setup.`
            );
          } else {
            message.error(`Failed to create purchase order: ${result.error}`);
          }
        } else {
          message.error(`Failed to create purchase order: ${result.error}`);
        }
      }
    } catch (error) {
      console.error("Error creating purchase order:", error);
      message.error("An error occurred while creating the purchase order");
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "concept":
        return "blue";
      case "expected":
        return "orange";
      case "purchased":
        return "green";
      case "cancelled":
        return "red";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case "concept":
        return "Draft";
      case "expected":
        return "Expected";
      case "purchased":
        return "Purchased";
      case "cancelled":
        return "Cancelled";
      default:
        return status;
    }
  };

  const columns = [
    {
      title: "Reference",
      dataIndex: "booking_reference",
      key: "booking_reference",
      render: (reference: string, record: PurchaseOrder) => (
        <Space>
          <Text strong>{reference || record.picqer_reference || "N/A"}</Text>
          <Tooltip title="View in Picqer (opens in new tab)">
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => {
                const picqerUrl = `https://e-freight-forwarding.picqer.com/purchaseorders/${record.picqer_purchase_order_id}`;
                window.open(picqerUrl, "_blank");
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
      render: (name: string) => name || "-",
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => formatDate(date),
    },
    {
      title: "Picqer IDs",
      key: "picqer_ids",
      render: (record: PurchaseOrder) => (
        <Space direction="vertical" size="small">
          <Tooltip
            title={`Click to view in Picqer (Global ID: ${record.picqer_purchase_order_id})`}
          >
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={() => {
                const picqerUrl = `https://e-freight-forwarding.picqer.com/purchaseorders/${record.picqer_purchase_order_id}`;
                window.open(picqerUrl, "_blank");
              }}
            >
              <Text type="secondary">
                Global: #{record.picqer_purchase_order_id}
              </Text>
            </Button>
          </Tooltip>
          {record.picqer_purchaseorderid && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Account: {record.picqer_purchaseorderid}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: PurchaseOrder) => {
        const canDelete =
          record.status === "concept" || record.status === "cancelled";
        const deleteTooltip = canDelete
          ? "Delete purchase order and all associated products"
          : `Cannot delete purchase order with status "${getStatusLabel(
              record.status
            )}". Please cancel it in Picqer first.`;

        return (
          <Space>
            <Tooltip title={deleteTooltip}>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingPurchaseOrder === record.id}
                disabled={!canDelete}
                onClick={() => {
                  Modal.confirm({
                    title: "Delete Purchase Order",
                    content: `Are you sure you want to delete purchase order "${
                      record.booking_reference
                    }" (Status: ${getStatusLabel(
                      record.status
                    )})? This will also delete all associated products and cannot be undone.`,
                    okText: "Delete",
                    okType: "danger",
                    cancelText: "Cancel",
                    onOk: () =>
                      handleDeletePurchaseOrder(record.id, record.status),
                  });
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {/* Info Alert for Storage Bookings */}
      {isStorageBooking ? (
        !picqerIntegrationValid && integrationError ? (
          <Alert
            message="Picqer Integration Required"
            description={`Cannot create purchase orders: ${integrationError}. Please configure the Picqer integration in the Picqer Integration tab first.`}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Alert
            message="Storage Booking - Purchase Orders Available"
            description="This booking is for warehouse storage. Purchase orders can be created manually to manage inventory in Picqer."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        )
      ) : (
        <Alert
          message="Delivery Booking - No Purchase Orders Needed"
          description="This booking has a delivery address. Purchase orders are only created for storage bookings."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            Purchase Orders
            {purchaseOrders.length > 0 && (
              <Tag color="blue">{purchaseOrders.length}</Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadPurchaseOrders}
              loading={loading}
            >
              Refresh
            </Button>
            {isStorageBooking && (
              <>
                <Button
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  onClick={handleCreatePurchaseOrder}
                  loading={creating}
                  disabled={
                    purchaseOrders.length > 0 || !picqerIntegrationValid
                  }
                  title={
                    !picqerIntegrationValid && integrationError
                      ? `Cannot create purchase order: ${integrationError}. Please configure in Picqer Integration tab.`
                      : purchaseOrders.length > 0
                      ? "Purchase order already exists"
                      : "Create purchase order for this storage booking"
                  }
                >
                  {purchaseOrders.length > 0
                    ? "Already Created"
                    : !picqerIntegrationValid
                    ? "Integration Required"
                    : "Create Purchase Order"}
                </Button>
              </>
            )}
          </Space>
        }
      >
        {purchaseOrders.length > 0 ? (
          <>
            <Table
              columns={columns}
              dataSource={purchaseOrders}
              rowKey="id"
              pagination={false}
              size="small"
              loading={loading}
            />

            {/* Additional Info */}
            <div
              style={{
                marginTop: 16,
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
              }}
            >
              <Text type="secondary">
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                Purchase orders are created manually after storage bookings are
                approved. They help manage inventory and fulfillment in Picqer.
              </Text>
            </div>
          </>
        ) : (
          <div
            style={{ textAlign: "center", padding: "40px 0", color: "#666" }}
          >
            <ShoppingCartOutlined
              style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }}
            />
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              No Purchase Orders
            </div>
            <div style={{ fontSize: 14 }}>
              {isStorageBooking
                ? "No purchase orders have been created for this storage booking yet."
                : "Purchase orders are only created for storage bookings."}
            </div>
          </div>
        )}

        {/* Product Analysis Section - Always show for storage bookings */}
        {isStorageBooking && (
          <div style={{ marginTop: 16 }}>
            {/* Debug info */}
            {(() => {
              console.log("Product analysis state:", {
                loadingProductAnalysis,
                productAnalysis: productAnalysis
                  ? {
                      total: productAnalysis.total,
                      ready: productAnalysis.ready,
                      needingSetup: productAnalysis.needingSetup,
                      productsCount: productAnalysis.products?.length,
                    }
                  : null,
              });
              return null;
            })()}
            {loadingProductAnalysis ? (
              <Alert
                message="Loading Product Analysis"
                description="Analyzing products for purchase order readiness..."
                type="info"
                showIcon
              />
            ) : productAnalysis ? (
              productAnalysis.total > 0 ? (
                <Alert
                  message="Product Analysis for Purchase Order"
                  description={
                    <div>
                      <div style={{ marginBottom: 12 }}>
                        <Text>
                          {productAnalysis.ready} of {productAnalysis.total}{" "}
                          products are ready for purchase orders.
                          {productAnalysis.needingSetup > 0 && (
                            <>
                              {" "}
                              <Text type="warning">
                                {productAnalysis.needingSetup} products need
                                setup.
                              </Text>
                            </>
                          )}
                        </Text>
                      </div>

                      {/* Product Status Table */}
                      <Table
                        size="small"
                        dataSource={productAnalysis.products}
                        rowKey="id"
                        pagination={false}
                        columns={[
                          {
                            title: "Product",
                            dataIndex: "name",
                            key: "name",
                            render: (name: string, record: ProductAnalysis) => (
                              <div>
                                <div>
                                  <Text strong>{name}</Text>
                                </div>
                                {record.ean && (
                                  <div>
                                    <Text
                                      type="secondary"
                                      style={{ fontSize: 12 }}
                                    >
                                      EAN: {record.ean}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            ),
                          },
                          {
                            title: "Quantity",
                            key: "quantity",
                            render: (record: ProductAnalysis) => {
                              const qty = Math.max(
                                record.pieces || 0,
                                record.cartons || 0,
                                1
                              );
                              return <Text>{qty}</Text>;
                            },
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            key: "status",
                            render: (status: string) => {
                              const statusConfig = {
                                ready: {
                                  color: "green",
                                  icon: <CheckCircleOutlined />,
                                  text: "Ready",
                                },
                                needs_picqer_link: {
                                  color: "orange",
                                  icon: <ExclamationCircleOutlined />,
                                  text: "Needs Picqer Link",
                                },
                                needs_company_product: {
                                  color: "orange",
                                  icon: <ExclamationCircleOutlined />,
                                  text: "Needs Company Product",
                                },
                                needs_ean: {
                                  color: "red",
                                  icon: <ExclamationCircleOutlined />,
                                  text: "Needs EAN",
                                },
                              };

                              const config =
                                statusConfig[
                                  status as keyof typeof statusConfig
                                ];
                              return (
                                <Tag color={config.color} icon={config.icon}>
                                  {config.text}
                                </Tag>
                              );
                            },
                          },
                          {
                            title: "Action Needed",
                            key: "action",
                            render: (record: ProductAnalysis) => {
                              if (record.canInclude)
                                return <Text type="success">✓ Ready</Text>;

                              switch (record.status) {
                                case "needs_ean":
                                  return (
                                    <Text type="secondary">
                                      Add EAN code to booking product
                                    </Text>
                                  );
                                case "needs_company_product":
                                  return (
                                    <Text type="secondary">
                                      Link to company catalog via EAN
                                    </Text>
                                  );
                                case "needs_picqer_link":
                                  return (
                                    <Text type="secondary">
                                      Link company product to Picqer
                                    </Text>
                                  );
                                default:
                                  return <Text type="secondary">-</Text>;
                              }
                            },
                          },
                        ]}
                      />
                    </div>
                  }
                  type={productAnalysis.needingSetup > 0 ? "warning" : "info"}
                  showIcon
                />
              ) : (
                <Alert
                  message="No Products Found"
                  description="This booking has no products to analyze for purchase order creation."
                  type="info"
                  showIcon
                />
              )
            ) : (
              <Alert
                message="No Product Analysis Available"
                description="Unable to load product analysis. Please try refreshing the page."
                type="warning"
                showIcon
              />
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default PurchaseOrderSection;
